import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from "@angular/core";
import { NovaButton } from "@rds-nova/angular-button";
import { NovaCard, NovaCardContent } from "@rds-nova/angular-card";
import { NovaIcon } from "@rds-nova/angular-icon";
import { NovaSkeleton } from "@rds-nova/angular-skeleton";
import { ClosingDetailEntry } from "../../+state/ron-monitor.store";
import {
  ClosingDetail,
  ClosingDetailClient,
  SlotVariant,
} from "../../models/ron-monitor.models";
import {
  NO_VALUE,
  PanelAction,
  formatClockTime,
  formatTimeRelativeTo,
  panelActions,
} from "../../ron-closing-monitor.utils";

export interface PanelRow {
  label: string;
  value: string;
}

interface SkeletonCard {
  titleWidth: string;
  rowWidths: string[];
}

const SKELETON_CARDS: readonly SkeletonCard[] = [
  { titleWidth: "48%", rowWidths: ["62%", "88%", "100%", "54%"] },
  { titleWidth: "44%", rowWidths: ["78%", "92%", "58%"] },
  { titleWidth: "42%", rowWidths: ["70%", "84%", "100%", "64%", "50%"] },
];

@Component({
  selector: "app-order-detail-panel",
  templateUrl: "./order-detail-panel.component.html",
  styleUrls: ["./order-detail-panel.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NovaButton,
    NovaCard,
    NovaCardContent,
    NovaIcon,
    NovaSkeleton,
  ],
  host: {
    class: "rcm-panel",
    role: "region",
    "[id]": "panelId",
    "[attr.aria-label]": "ariaLabel",
    "[attr.aria-busy]": "showLoading || retrying || null",
    "(keydown.escape)": "onEscape($event)",
  },
})
export class OrderDetailPanelComponent implements OnChanges {
  @Input({ required: true }) panelId!: string;
  @Input({ required: true }) variant!: SlotVariant;
  @Input() ariaLabel = "Closing details";
  @Input() entry: ClosingDetailEntry | null = null;
  @Input() panelActionsEnabled = false;

  @Output() retry = new EventEmitter<void>();
  @Output() dismiss = new EventEmitter<void>();

  readonly skeletonCards = SKELETON_CARDS;

  detail: ClosingDetail | null = null;
  errorMessage: string | null = null;
  showLoading = false;
  retrying = false;
  actions: PanelAction[] = [];
  notaryRows: PanelRow[] = [];
  clientGroups: PanelRow[][] = [];
  closingRows: PanelRow[] = [];

  ngOnChanges(): void {
    this.detail = this.entry?.detail ?? null;
    this.errorMessage = this.entry?.error ?? null;
    this.showLoading = this.detail === null && this.errorMessage === null;
    this.retrying = (this.entry?.loading ?? false) && this.errorMessage !== null;
    this.actions = this.panelActionsEnabled ? panelActions(this.variant) : [];
    this.notaryRows = this.detail ? this.buildNotaryRows(this.detail) : [];
    this.clientGroups = this.detail
      ? this.detail.clients.map((client) => this.buildClientRows(client))
      : [];
    this.closingRows = this.detail ? this.buildClosingRows(this.detail) : [];
  }

  onEscape(event: Event): void {
    event.stopPropagation();
    this.dismiss.emit();
  }

  trackByRowLabel(_index: number, row: PanelRow): string {
    return row.label;
  }

  trackByGroupIndex(index: number): number {
    return index;
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByActionLabel(_index: number, action: PanelAction): string {
    return action.label;
  }

  private buildNotaryRows(detail: ClosingDetail): PanelRow[] {
    const rows: PanelRow[] = [
      { label: "Status", value: detail.notary.status },
      { label: "Currently", value: detail.notary.currently },
    ];

    if (detail.notary.reason !== null) {
      rows.push({ label: "Reason", value: detail.notary.reason });
    }
    if (detail.notary.phone !== null) {
      rows.push({ label: "Phone", value: detail.notary.phone });
    }
    return rows;
  }

  private buildClientRows(client: ClosingDetailClient): PanelRow[] {
    const rows: PanelRow[] = [
      { label: "Name", value: `${client.name} (${client.role})` },
      { label: "Currently", value: client.currently },
    ];

    if (client.phone !== null) {
      rows.push({ label: "Phone", value: client.phone });
    }
    return rows;
  }

  private buildClosingRows(detail: ClosingDetail): PanelRow[] {
    return [
      { label: "Loan number", value: detail.closing.loanNumber },
      { label: "Signing ID", value: detail.closing.signingId },
      { label: "Property address", value: detail.closing.propertyAddress },
      this.timingRow(detail),
    ];
  }

  private timingRow(detail: ClosingDetail): PanelRow {
    const scheduled = new Date(detail.closing.scheduledAt);

    if (detail.closing.completedAt !== null) {
      return {
        label: "Completed at",
        value: this.atTime(detail.closing.completedAt, scheduled),
      };
    }
    if (detail.closing.rescheduledTo !== null) {
      return {
        label: "Rescheduled to",
        value: this.atTime(detail.closing.rescheduledTo, scheduled),
      };
    }
    return {
      label: "Scheduled",
      value: this.atTime(detail.closing.scheduledAt, scheduled),
    };
  }

  private atTime(value: string, reference: Date): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return NO_VALUE;
    }
    return Number.isNaN(reference.getTime())
      ? formatClockTime(parsed)
      : formatTimeRelativeTo(parsed, reference);
  }
}
